// ══════════════════════════════════════════════════════════════════════════════════════════════
//  t667 · NADA AL CARGAR PUEDE DEJAR LA PANTALLA DE «BIANGO» PARA SIEMPRE (3-nov-2026)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  El dueño: «puse a una clínica fisioterapia, pero al entrar a la clínica se queda en Biango y no carga». Dos maneras de
//  quedarse ahí, y las dos se cierran aquí:
//   · UN FALLO AL PINTAR fuera de la parte central (el menú, la cabecera, un aviso por encima de las rutas): solo la parte
//     central (el <Outlet/> de Layout y AdminLayout) tenía su ErrorBoundary; lo de fuera tiraba la aplicación ENTERA y la
//     página se quedaba en blanco, con «Biango» en la pestaña. App.jsx envuelve ahora todo en `ErrorAlCargar`.
//   · UNA ESPERA QUE NO ACABA (components/PuertaDeEntrada): la marca de la clínica que no llega en su plazo.
//  En los dos casos sale lo mismo: qué ha pasado, en palabras, el mensaje técnico para soporte y «Volver a intentar».
//  Nunca una pantalla de carga sin salida.
import { Component } from 'react'
import { t } from '../utils/i18n'
import { esFalloDeCarga, recargarPorVersionNueva } from './ErrorBoundary'

const fuera = {
  minHeight: 'var(--alto-pantalla)', display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: '24px 16px', background: 'var(--bg, #f8fafc)', boxSizing: 'border-box',
}
const tarjeta = {
  width: '100%', maxWidth: '520px', background: 'var(--surface, #fff)', border: '1px solid var(--border, #e2e8f0)',
  borderRadius: '14px', padding: '26px 24px', boxShadow: '0 8px 30px rgba(15, 23, 42, .08)', color: 'var(--text-primary, #0f172a)',
}
const boton = (principal) => ({
  padding: '10px 16px', borderRadius: '10px', fontSize: '14px', fontWeight: 700, cursor: 'pointer',
  border: principal ? 'none' : '1px solid var(--border, #cbd5e1)',
  background: principal ? 'var(--primary, #1668c4)' : 'var(--surface, #fff)',
  color: principal ? 'var(--sobre-primary, #fff)' : 'var(--text-primary, #0f172a)',
})

// La pantalla, sola: la pinta el ErrorBoundary de abajo y también PuertaDeEntrada cuando la espera se pasa de su plazo.
export function PantallaErrorDeCarga({ titulo, detalle, mensaje, onReintentar }) {
  const recargar = () => { try { window.location.reload() } catch (_) { /* sin ventana */ } }
  return (
    <div style={fuera} data-error-al-cargar="">
      <div style={tarjeta} role="alert">
        <div style={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-.4px', marginBottom: '8px' }}>
          {titulo || t('components.ErrorAlCargar.titulo', 'No se ha podido abrir Biango')}
        </div>
        <div style={{ fontSize: '14.5px', lineHeight: 1.55, color: 'var(--text-secondary, #475569)', marginBottom: mensaje ? '12px' : '18px' }}>
          {detalle || t('components.ErrorAlCargar.detalle', 'Algo ha fallado al cargar esta pantalla. Lo que ya estaba guardado no se ha perdido. Vuelve a intentarlo y, si se repite, pasa a soporte el mensaje de abajo.')}
        </div>
        {mensaje ? (
          <div data-error-al-cargar-mensaje="" style={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: '12.5px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: 'var(--surface-2, #f1f5f9)', border: '1px solid var(--border, #e2e8f0)', borderRadius: '8px', padding: '10px 12px', marginBottom: '18px', color: 'var(--text-primary, #0f172a)' }}>
            {mensaje}
          </div>
        ) : null}
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button type="button" data-volver-a-intentar="" onClick={onReintentar || recargar} style={boton(true)}>
            {t('components.ErrorAlCargar.volver_a_intentar', 'Volver a intentar')}
          </button>
          <button type="button" data-recargar-pagina="" onClick={recargar} style={boton(false)}>
            {t('components.ErrorAlCargar.recargar', 'Recargar la página')}
          </button>
        </div>
      </div>
    </div>
  )
}

// Lo que se enseña de un error: su mensaje, o lo que sea que se haya lanzado, en una línea legible (nunca «[object Object]»).
export function mensajeDe(error) {
  if (error == null) return ''
  if (typeof error === 'string') return error
  const m = error && (error.message || error.name)
  if (m) return String(m)
  try { return JSON.stringify(error) } catch (_) { return String(error) }
}

// (Exportada con nombre y no «default»: así la cargan las guardias que ejecutan el fichero, test-helpers/jsxEjecutable.)
export class ErrorAlCargar extends Component {
  constructor(props) {
    super(props)
    this.state = { error: null, intento: 0 }
    this.reintentar = this.reintentar.bind(this)
  }
  static getDerivedStateFromError(error) {
    return { error: error || new Error('Error desconocido') }
  }
  componentDidCatch(error, info) {
    try { console.error('[ErrorAlCargar]', error, info && info.componentStack) } catch (_) { /* sin consola */ }
    // Una versión nueva a medio llegar (falta un trozo de la anterior): la recarga la arregla sola, una vez (ErrorBoundary).
    if (esFalloDeCarga(error)) recargarPorVersionNueva()
  }
  // «Volver a intentar» MONTA DE NUEVO lo de dentro (otra `key`): lo que falló vuelve a pedir sus datos desde cero.
  reintentar() {
    this.setState((s) => ({ error: null, intento: (s.intento || 0) + 1 }))
  }
  render() {
    if (this.state.error) {
      return <PantallaErrorDeCarga mensaje={mensajeDe(this.state.error)} onReintentar={this.reintentar} />
    }
    return <ConClave key={this.state.intento}>{this.props.children}</ConClave>
  }
}

function ConClave({ children }) { return children }
